<template>
  <div class="admin-fte-budget-tab">
    <div class="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h2 class="text-base font-semibold text-gray-900 dark:text-white sm:text-lg">
          FTE-doelstellingen
        </h2>
        <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Vul per team en functieniveau de gewenste FTE per maand in. Dit is geen financieel budget.
        </p>
      </div>

      <div class="flex flex-wrap items-end gap-3">
        <label class="flex flex-col text-xs font-medium text-gray-500 dark:text-gray-400">
          Jaar
          <select
            v-model.number="selectedYear"
            class="mt-1 h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
          >
            <option v-for="yearOption in yearOptions" :key="yearOption" :value="yearOption">
              {{ yearOption }}
            </option>
          </select>
        </label>

        <button
          type="button"
          class="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-blue-600 px-3 text-sm font-medium text-white transition-colors hover:bg-blue-700"
          @click="addRow"
        >
          <PlusIcon class="h-4 w-4" />
          Rij toevoegen
        </button>
        <button
          type="button"
          :disabled="isSaving === 'all' || matrixRows.length === 0"
          class="inline-flex h-10 items-center justify-center rounded-lg bg-teal-600 px-4 text-sm font-medium text-white transition-colors hover:bg-teal-700 disabled:cursor-not-allowed disabled:bg-teal-300"
          @click="saveAllRows"
        >
          {{ isSaving === 'all' ? 'Opslaan...' : 'Wijzigingen opslaan' }}
        </button>
      </div>
    </div>

    <div
      v-if="saveError"
      class="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300"
    >
      {{ saveError }}
    </div>

    <div v-if="isLoading" class="py-12 text-center">
      <div
        class="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600"
      ></div>
      <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">FTE-doelstellingen laden...</p>
    </div>

    <div v-else-if="error" class="py-12 text-center text-sm text-gray-500 dark:text-gray-400">
      FTE-doelstellingen konden niet worden geladen.
      <button class="ml-2 text-blue-600 hover:underline" @click="refetch()">
        Opnieuw proberen
      </button>
    </div>

    <div
      class="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800"
    >
      <div
        v-if="matrixRows.length === 0"
        class="px-4 py-12 text-center text-sm text-gray-500 dark:text-gray-400"
      >
        Nog geen FTE-doelstellingen voor {{ selectedYear }}. Klik op ‘Rij toevoegen’.
      </div>

      <div v-else class="overflow-hidden">
        <table class="w-full table-fixed border-collapse text-sm">
          <thead class="text-xs text-gray-600 dark:text-gray-300">
            <tr class="bg-gray-100 dark:bg-gray-900/60">
              <th
                rowspan="2"
                class="w-[16%] border-b border-gray-200 px-3 py-2 text-left font-semibold dark:border-gray-700"
              >
                Team
              </th>
              <th
                rowspan="2"
                class="w-[15%] border-b border-gray-200 px-3 py-2 text-left font-semibold dark:border-gray-700"
              >
                Functie / niveau
              </th>
              <th
                colspan="12"
                class="border-b border-l border-gray-200 px-3 py-2 text-center font-semibold dark:border-gray-700"
              >
                {{ selectedYear }}
              </th>
            </tr>
            <tr class="bg-gray-50 dark:bg-gray-900/30">
              <th
                v-for="month in monthOptions"
                :key="month.value"
                class="w-[5.75%] border-b border-l border-gray-200 px-1 py-2 text-center font-medium uppercase dark:border-gray-700"
              >
                {{ month.shortLabel }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
            <tr
              v-for="row in matrixRows"
              :key="row.localKey"
              class="odd:bg-white even:bg-gray-50/60 dark:odd:bg-gray-800 dark:even:bg-gray-700/20"
            >
              <td class="px-3 py-2 align-middle">
                <select
                  v-model="row.team_id"
                  :disabled="row.persisted"
                  class="w-full min-w-0 rounded-md border border-gray-200 bg-white px-1.5 py-1.5 text-xs text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 disabled:cursor-not-allowed disabled:bg-gray-100 dark:disabled:bg-gray-700"
                  :aria-label="`Team rij ${row.localKey}`"
                  @change="syncTeamName(row)"
                >
                  <option value="">Selecteer team</option>
                  <option v-for="team in teamOptions" :key="team.team_id" :value="team.team_id">
                    {{ team.team_name }}
                  </option>
                </select>
              </td>
              <td class="px-3 py-2 align-middle">
                <select
                  v-model="row.function_level"
                  :disabled="row.persisted"
                  class="w-full min-w-0 rounded-md border border-gray-200 bg-white px-1.5 py-1.5 text-xs text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 disabled:cursor-not-allowed disabled:bg-gray-100 dark:disabled:bg-gray-700"
                  :aria-label="`Functie of niveau rij ${row.localKey}`"
                >
                  <option value="">Selecteer functie / niveau</option>
                  <option v-for="level in functionLevelOptions" :key="level" :value="level">
                    {{ level }}
                  </option>
                </select>
              </td>
              <td
                v-for="month in monthOptions"
                :key="month.value"
                class="border-l border-gray-100 px-2 py-2 dark:border-gray-700"
              >
                <input
                  v-model.number="row.values[month.value]"
                  type="number"
                  min="0"
                  step="0.1"
                  class="w-full min-w-0 rounded-md border border-gray-200 bg-white px-1 py-1.5 text-right text-xs text-gray-700 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                  :aria-label="`FTE ${month.label} ${row.function_level || 'nieuwe rij'}`"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">
      Vul de rijen in en klik daarna bovenaan op ‘Wijzigingen opslaan’. Alle ingevulde maanden
      worden in één keer opgeslagen.
    </p>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { PlusIcon } from '@heroicons/vue/24/outline'
import { workforceForecastService } from '@/services/workforceForecastService.js'

const MONTH_NAMES = [
  'januari',
  'februari',
  'maart',
  'april',
  'mei',
  'juni',
  'juli',
  'augustus',
  'september',
  'oktober',
  'november',
  'december'
]

const currentYear = new Date().getFullYear()
const selectedYear = ref(currentYear)
const matrixRows = ref([])
const nextLocalKey = ref(1)
const isSaving = ref(null)
const saveError = ref(null)

const monthOptions = MONTH_NAMES.map((label, index) => ({
  value: index + 1,
  label,
  shortLabel: label.slice(0, 3)
}))

const { data, isLoading, error, refetch } = useQuery({
  queryKey: computed(() => ['fte-budget-targets', selectedYear.value]),
  queryFn: () => workforceForecastService.getFteBudgetTargets(selectedYear.value),
  staleTime: 1000 * 60 * 5
})

const { data: optionsData } = useQuery({
  queryKey: ['fte-budget-options'],
  queryFn: () => workforceForecastService.getFteBudgetOptions(),
  staleTime: 1000 * 60 * 30
})

const allRows = computed(() => (Array.isArray(data.value) ? data.value : []))

const hasGlobalOptions = computed(
  () => Array.isArray(optionsData.value?.teams) && Array.isArray(optionsData.value?.function_levels)
)

const rows = computed(() => {
  if (!hasGlobalOptions.value) return allRows.value

  const teamIds = new Set(optionsData.value.teams.map(team => team.team_id))
  const functionLevels = new Set(optionsData.value.function_levels)
  return allRows.value.filter(
    row => teamIds.has(row.team_id) && functionLevels.has(row.function_level)
  )
})

const yearOptions = computed(() => {
  const years = new Set()
  for (let offset = -2; offset <= 2; offset += 1) years.add(currentYear + offset)
  for (const row of rows.value) {
    if (row.year != null) years.add(Number(row.year))
  }
  return [...years].sort((a, b) => a - b)
})

const teamOptions = computed(() => {
  if (hasGlobalOptions.value) {
    return [...optionsData.value.teams].sort((a, b) => a.team_name.localeCompare(b.team_name, 'nl'))
  }

  const options = new Map()
  for (const row of allRows.value) {
    if (row.team_id && !options.has(row.team_id)) {
      options.set(row.team_id, { team_id: row.team_id, team_name: row.team_name })
    }
  }
  return [...options.values()].sort((a, b) => a.team_name.localeCompare(b.team_name, 'nl'))
})

const functionLevelOptions = computed(() => {
  if (hasGlobalOptions.value) {
    return [...optionsData.value.function_levels].sort((a, b) => a.localeCompare(b, 'nl'))
  }

  return [...new Set(allRows.value.map(row => row.function_level).filter(Boolean))].sort((a, b) =>
    a.localeCompare(b, 'nl')
  )
})

function emptyValues() {
  return Object.fromEntries(monthOptions.map(month => [month.value, 0]))
}

function buildMatrixRows(sourceRows) {
  const grouped = new Map()
  for (const row of sourceRows) {
    const key = `${row.team_id}||${row.function_level}`
    if (!grouped.has(key)) {
      grouped.set(key, {
        localKey: key,
        team_id: row.team_id,
        team_name: row.team_name,
        function_level: row.function_level,
        persisted: true,
        values: emptyValues()
      })
    }
    grouped.get(key).values[Number(row.month)] = Number(row.fte_target) || 0
  }
  matrixRows.value = [...grouped.values()]
}

watch(rows, buildMatrixRows, { immediate: true, deep: true })

function addRow() {
  const key = `new-${nextLocalKey.value}`
  nextLocalKey.value += 1
  matrixRows.value.push({
    localKey: key,
    team_id: '',
    team_name: '',
    function_level: '',
    persisted: false,
    values: emptyValues()
  })
}

function syncTeamName(row) {
  row.team_name = teamOptions.value.find(team => team.team_id === row.team_id)?.team_name || ''
}

async function saveAllRows() {
  if (matrixRows.value.length === 0) return

  const seen = new Set()
  const targets = []
  for (const row of matrixRows.value) {
    const team = teamOptions.value.find(option => option.team_id === row.team_id)
    if (!team || !row.function_level) {
      saveError.value = 'Selecteer voor iedere rij een team en een functie of niveau.'
      return
    }

    const rowKey = `${row.team_id}||${row.function_level}`
    if (seen.has(rowKey)) {
      saveError.value = 'Iedere combinatie van team en functie/niveau mag maar één keer voorkomen.'
      return
    }
    seen.add(rowKey)

    for (const month of monthOptions) {
      const fteTarget = Number(row.values[month.value])
      if (!Number.isFinite(fteTarget) || fteTarget < 0) {
        saveError.value = `Ongeldige of negatieve FTE in ${month.label} voor ${team.team_name} / ${row.function_level}.`
        return
      }
      targets.push({
        team_id: row.team_id,
        team_name: team.team_name,
        function_level: row.function_level,
        month: month.value,
        fte_target: fteTarget
      })
    }
  }

  isSaving.value = 'all'
  saveError.value = null
  try {
    await workforceForecastService.bulkUpsertFteBudgetTargets(Number(selectedYear.value), targets)
    await refetch()
  } catch (err) {
    saveError.value = err.response?.data?.error || 'Fout bij opslaan van FTE-doelstellingen.'
  } finally {
    isSaving.value = null
  }
}
</script>

<style scoped>
input[type='number']::-webkit-outer-spin-button,
input[type='number']::-webkit-inner-spin-button {
  margin: 0;
  -webkit-appearance: none;
}

input[type='number'] {
  -moz-appearance: textfield;
}
</style>
